import { useState, useEffect } from "react";
import { X } from "lucide-react";

const HOTEL_WHATSAPP = "919422476222";

export default function BookingModal({ onClose, selectedRoom, rooms = [] }) {

  // Dates
  const todayDate = new Date();
  const tomorrowDate = new Date();
  tomorrowDate.setDate(todayDate.getDate() + 1);

  const today = todayDate.toISOString().split("T")[0];
  const tomorrow = tomorrowDate.toISOString().split("T")[0];

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    checkin: today,
    checkout: tomorrow,
    roomId: selectedRoom?.id || rooms[0]?.id || "",
    roomType: "ac",
    extraGuests: 0,
    requests: "",
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Sync selected room when modal opens
  useEffect(() => {
    if (selectedRoom) {
      setForm((prev) => ({
        ...prev,
        roomId: selectedRoom.id,
        roomType: selectedRoom.price?.ac ? "ac" : "nonAc",
        extraGuests: 0,
      }));
    }
  }, [selectedRoom]);

  // Selected room
  const selected = rooms.find((r) => r.id === Number(form.roomId));

  // Availability
  const hasAC = !!selected?.price?.ac;
  const hasNonAC = !!selected?.price?.nonAc;

  // Auto fallback if AC not available
  useEffect(() => {
    if (!hasAC && form.roomType === "ac") {
      setForm((prev) => ({ ...prev, roomType: "nonAc" }));
    }
  }, [form.roomId, hasAC]);

  // Guests logic
  const getDefaultGuests = (room) => {
    if (!room) return 2;
    const match = room.beds?.match(/\d+/);
    return match ? Number(match[0]) : 2;
  };

  const defaultGuests = getDefaultGuests(selected);
  const totalGuests = defaultGuests + Number(form.extraGuests || 0);

  // Price
  const price =
    selected?.price?.[form.roomType] ||
    selected?.price?.nonAc ||
    "N/A";

  // Submit
  const handleSubmit = () => {
    if (!form.name || !form.phone) {
      alert("Please fill required fields");
      return;
    }

    const message = `
📌 *New Booking Inquiry*
    **Dreamlandinn**

👤 Name: ${form.name}
📱 Phone: ${form.phone}
📧 Email: ${form.email}

📅 Check-in: ${form.checkin}
📅 Check-out: ${form.checkout}

🏨 Room: ${selected?.name}
🛏️ Beds: ${selected?.beds || "Couple"}
❄️ Type: ${form.roomType.toUpperCase()}
💰 Price: ₹${price}

👥 Guests: ${totalGuests}
(Default: ${defaultGuests}, Extra: ${form.extraGuests})

📝 Requests:
${form.requests || "None"}
    `;

    window.open(
      `https://wa.me/${HOTEL_WHATSAPP}?text=${encodeURIComponent(message)}`,
      "_blank"
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center px-3 py-6">
        <div className="relative bg-white w-full max-w-2xl p-6 md:p-10 shadow-2xl rounded-3xl">

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-[#EEF6FF] hover:bg-[#F5C518] hover:text-[#1B3D6E] rounded-full p-2 transition-colors duration-300"
          >
            <X size={18} />
          </button>

          <h2 className="text-2xl md:text-3xl text-center font-['Playfair_Display'] font-semibold text-[#1B3D6E] mb-6">
            Book Your Stay
          </h2>

          <div className="space-y-4">

            {/* Name */}
            <input
              name="name"
              placeholder="Your Name"
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
            />

            {/* Email + Phone */}
            <div className="grid md:grid-cols-2 gap-3">
              <input
                name="email"
                placeholder="Email"
                onChange={handleChange}
                className="border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
              />
              <input
                name="phone"
                placeholder="Phone"
                onChange={handleChange}
                className="border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
              />
            </div>

            {/* Dates */}
            <div className="grid md:grid-cols-2 gap-3">
              <input
                type="date"
                name="checkin"
                value={form.checkin}
                min={today}
                onChange={handleChange}
                className="border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
              />
              <input
                type="date"
                name="checkout"
                value={form.checkout}
                min={form.checkin}
                onChange={handleChange}
                className="border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
              />
            </div>

            {/* Room Selector */}
            <select
              name="roomId"
              value={form.roomId}
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-full px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
            >
              {rooms.map((room) => (
                <option key={room.id} value={room.id}>
                  {room.name} ({room.beds || "Couple"})
                </option>
              ))}
            </select>

            {/* Guests */}
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#2478C8] font-medium ml-2">
                  Extra Guests (above {defaultGuests})
                </label>
                <input
                  type="number"
                  name="extraGuests"
                  min={0}
                  value={form.extraGuests}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-full px-4 py-3 mt-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
                />
              </div>
              <div className="flex flex-col justify-center text-sm text-gray-600">
                <p>Default: <span className="text-[#1B3D6E] font-medium">{defaultGuests}</span></p>
                <p>Total: <span className="text-[#1B3D6E] font-medium">{totalGuests}</span></p>
              </div>
            </div>

            {/* AC / Non-AC */}
            <div className="flex gap-3">
              {hasAC && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, roomType: "ac" })}
                  className={`flex-1 py-2 rounded-full font-medium transition-colors duration-200 ${
                    form.roomType === "ac"
                      ? "bg-[#1B3D6E] text-[#F5C518]"
                      : "bg-[#EEF6FF] text-[#1B3D6E] hover:bg-[#1B3D6E]/10"
                  }`}
                >
                  AC
                </button>
              )}
              {hasNonAC && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, roomType: "nonAc" })}
                  className={`flex-1 py-2 rounded-full font-medium transition-colors duration-200 ${
                    form.roomType === "nonAc"
                      ? "bg-[#1B3D6E] text-[#F5C518]"
                      : "bg-[#EEF6FF] text-[#1B3D6E] hover:bg-[#1B3D6E]/10"
                  }`}
                >
                  Non-AC
                </button>
              )}
            </div>

            {!hasAC && (
              <p className="text-xs text-red-500 text-center">
                Only Non-AC available
              </p>
            )}

            {/* Price */}
            <div className="text-center text-xl font-semibold text-[#1B3D6E]">
              ₹ <span className="text-[#F5C518]">{price}</span> / night
            </div>

            {/* Special Requests */}
            <textarea
              name="requests"
              placeholder="Special requests..."
              onChange={handleChange}
              className="w-full border border-gray-200 rounded-2xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2478C8]/30 focus:border-[#2478C8] transition-colors"
            />

            {/* Submit */}
            <button
              onClick={handleSubmit}
              className="w-full bg-[#F5C518] text-[#1B3D6E] py-3 rounded-full font-semibold hover:bg-[#D4A010] transition-colors duration-200"
            >
              Send Inquiry
            </button>

          </div>
        </div>
      </div>
    </div>
  );
}